import React from 'react';
import { useIntl } from 'react-intl';

import { useCurrentTheme } from 'app/shared/theme';
import { useIsBreakpointSize } from 'app/shared/utils';
import { formattedEventDateAndTime } from 'app/shared/utils/eventDateTime';
import { Event } from 'app/shared/typings';
import { Box, Typography } from 'app/shared/components/atoms';
import { neighborhoodOrSecretLocation } from 'app/fan/utils';
import { useEventPageContext } from 'app/fan/context/EventPage';

import { AboutConcert } from './AboutConcert';
import { AddressLink } from './AddressLink';
import { MerchandisingProperties } from './MerchandisingProperties';
import { NeighborhoodLink } from './NeighborhoodLink';

interface EventInfoProps {
  eventStartsAt: string;
  guestsArriveAt: Date | string;
  timezone?: string;
  cityTitle: string;
  countryCode: string;
  neighborhoodTitle: string;
  closestStation?: string;
  venueAddress?: string;
  revealLocation?: boolean;
  ticketsUnavailable?: boolean;
  event: Event;
}

export const EventInfo: React.FC<React.PropsWithChildren<{ event: Event }>> = ({
  event,
}) => {
  const { dimensions } = useCurrentTheme();
  const { formatMessage } = useIntl();
  const neighborhoodTitle = neighborhoodOrSecretLocation(event, formatMessage);
  const { eventAttendee } = useEventPageContext();

  const userTickets =
    eventAttendee && eventAttendee.visibleToUser && eventAttendee.tickets;
  const userHasTickets = userTickets && userTickets.length > 0;

  const { isVenueConfirmed, venue } = event;

  const venueAddress =
    isVenueConfirmed && userHasTickets ? venue?.address : undefined;
  const closestStation = isVenueConfirmed ? venue?.closestStation : undefined;

  return (
    <Box flexDirection="column" mx={0} pb={4}>
      <Box px={dimensions.horizontalPadding.pageGridItem}>
        <EventDetailsContent
          eventStartsAt={event.startsAt}
          guestsArriveAt={event.guestsArriveAt}
          timezone={event.city.timezone}
          cityTitle={event.city.title}
          countryCode={event.city.country.countryCode}
          neighborhoodTitle={neighborhoodTitle}
          closestStation={closestStation}
          venueAddress={venueAddress}
          ticketsUnavailable={!event.isAvailable}
          event={event}
        />
      </Box>
    </Box>
  );
};

const EventDetailsContent: React.FC<
  React.PropsWithChildren<EventInfoProps>
> = ({
  eventStartsAt,
  guestsArriveAt,
  timezone,
  cityTitle,
  countryCode,
  neighborhoodTitle,
  closestStation,
  venueAddress,
  event,
  ticketsUnavailable = false,
}) => {
  const { formatMessage } = useIntl();
  const { colors } = useCurrentTheme();
  const { isMobile } = useIsBreakpointSize();

  const { eventDate: doorsOpenDate, eventTime: doorsOpenTime } =
    formattedEventDateAndTime(guestsArriveAt, timezone, countryCode);

  const { eventTime: eventStartTime } = formattedEventDateAndTime(
    event.startsAt,
    timezone,
    countryCode
  );

  if (ticketsUnavailable) {
    return (
      <Box py={6} bb={colors.accent.primaryAccent.base}>
        <Typography
          fontWeight="medium"
          display="block"
          fontFamily="title"
          color={colors.primary.white.base}
          fontSize="fontSize500"
        >
          {doorsOpenDate}
        </Typography>
        <Typography
          fontSize="fontSize400"
          fontWeight="medium"
          display="block"
          color={colors.primary.white.base}
          data-qaid="event-page-event-details-neighborhood"
        >
          {neighborhoodTitle}
        </Typography>
      </Box>
    );
  }

  return (
    <Box pb={6}>
      <AboutConcert event={event} />
      <MerchandisingProperties event={event} />
      <Box
        justifyContent="space-between"
        alignItems={isMobile ? 'flex-start' : 'center'}
        flexDirection={isMobile ? 'column' : 'row'}
        pb={3}
        pt={3}
      >
        <Typography
          color={colors.primary.white.base}
          fontSize="fontSize500"
          fontWeight="medium"
          fontFamily="title"
        >
          {doorsOpenDate}
        </Typography>
        <Box flexDirection="column" alignItems="flex-start">
          <Typography
            color={colors.primary.white.base}
            fontSize="fontSize100"
            fontWeight="medium"
          >
            {formatMessage({ id: 'event.eventDetails.doorsOpen' })}
          </Typography>
          <Typography
            color={colors.primary.white.base}
            fontSize="fontSize500"
            fontWeight="medium"
            fontFamily="title"
          >
            {doorsOpenTime}
          </Typography>
          <Typography
            color={colors.accent.primaryAccent.base}
            fontSize="fontSize100"
            fontWeight="medium"
          >
            {formatMessage(
              {
                id: 'event.eventDetails.showStartsAt',
              },
              {
                showStartsAt: eventStartTime,
              }
            )}
          </Typography>
        </Box>
      </Box>
      <NeighborhoodLink
        neighborhoodTitle={neighborhoodTitle}
        cityTitle={cityTitle}
        data-qaid="event-page-event-details-neighborhood-link"
      />
      {closestStation && (
        <Box mt={1}>
          <Typography
            display="inline"
            color={colors.primary.white.base}
            fontStyle="italic"
          >
            {formatMessage({ id: 'event.eventDetails.nearestStation' })}
          </Typography>
          <Typography
            color={colors.primary.white.base}
            data-qaid="event-page-event-details-closest-station"
            display="inline"
          >
            {closestStation}
          </Typography>
        </Box>
      )}
      <AddressLink
        venueAddress={venueAddress}
        eventStartsAt={eventStartsAt}
        data-qaid="event-page-event-details-address-link"
      />
    </Box>
  );
};
